import { useRef } from "react";
import {
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Box,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { ButtonVerifyStyle } from "../../../app/styles/styles";
import { Controller, Control } from "react-hook-form";
import { colors } from "../../../styles/colors";
import fontWeight from "../../../styles/mui/fontWeight";
import { maxFileSize } from "../../../constants/Config";

interface FileUploadFieldProps {
  setFile: (file?: File) => void;
  file?: File;
  control: Control<any>;
  name: string;
}

const FileUploadField: React.FC<FileUploadFieldProps> = ({
  setFile,
  control,
  name,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const MAX_SIZE = Number(maxFileSize) * 1000000;
    const maxFileSizeInMegaBytes = MAX_SIZE
      ? MAX_SIZE / 1000000
      : 1000000;
  const handleChooseClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRemoveFile = (onChange: (file?: File) => void) => {
    setFile(undefined);
    onChange(undefined);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Box>
      <Controller
        name={name}
        control={control}
        rules={{
          required: "Document is required",
          validate: (file: File | undefined) => {
            if (!file) return "Document is required";
            if (file.type !== "application/pdf")
              return "Only PDF files are allowed";
            if (file.size <= 0) return "File size must be greater than 0 bytes";
            if (file.size > maxFileSizeInMegaBytes) return `File size must be less than 75MB`;
            return true;
          },
        }}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <>
            <TextField
              fullWidth
              name={name}
              label={
                <Box display="flex" alignItems="center">
                  <Typography
                  variant="h5"
                  fontWeight={fontWeight[3]}
                  color={colors.fonts[2]}
                  >
                  Upload Document
                  </Typography>
                  <Typography color="error"   variant="h4"
                  fontWeight={fontWeight[4]} ml={0.5}>
                  *
                  </Typography>
                </Box>
                }
                value={value ? value.name : ""}
                placeholder="No file selected"
                error={!!error}
                helperText={error ? error.message : ""}
                variant="standard"
                slotProps={{
                inputLabel: {
                  shrink: true,
                },
                input: {
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      {value ? (
                        <IconButton onClick={() => handleRemoveFile(onChange)}>
                          <CloseIcon />
                        </IconButton>
                      ) : (
                        <Box pb={2}>
                          <Button
                            variant="contained"
                            onClick={handleChooseClick}
                            sx={ButtonVerifyStyle}
                          >
                            Choose File
                          </Button>
                        </Box>
                      )}
                    </InputAdornment>
                  ),
                },
              }}
            />
            <input
              type="file"
              hidden
              ref={fileInputRef}
              onChange={(e) => {
                const files = e.target.files;
                if (!files || files.length === 0) return;
                const selectedFile = files[0];
                setFile(selectedFile);
                onChange(selectedFile);
              }}
              accept="application/pdf"
            />
          </>
        )}
      />
    </Box>
  );
};

export default FileUploadField;
